import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@readsavor/api-client';
import { useAuthStore, userQueryKeys } from '@readsavor/auth';
/**
 * Provides a mutation for resetting a user's subscription to the free plan.
 *
 * This hook simplifies the API call for the UI layer. Similar to the user
 * initialization hook, it now includes an `onSuccess` callback to manually
 * invalidate and refetch all user-related queries. This is necessary because
 * some backend operations might not trigger real-time updates reliably,
 * ensuring the UI reflects the new subscription state immediately.
 *
 * UI components can pass their own `onSuccess`, `onError` callbacks to handle
 * feedback like toast notifications.
 *
 * @example
 * const { mutate: reset, isLoading } = useSubscriptionReset({
 *   onSuccess: () => showToast('Subscription reset successful!'),
 *   onError: (error) => showToast(`Reset failed: ${error.message}`),
 * });
 *
 * reset({});
 */
export const useSubscriptionReset = (options) => {
    const queryClient = useAuthStore(state => state.queryClient);
    return useMutation({
        mutationFn: (payload) => apiClient.integration.resetSubscriptionToFree(payload),
        onSuccess: (data, variables, context) => {
            // Invalidate and refetch all user-related data to ensure UI consistency
            // after a significant state change like a subscription reset.
            if (queryClient) {
                queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
                queryClient.refetchQueries({ queryKey: userQueryKeys.all });
            }
            else {
                console.warn('[useSubscriptionReset] QueryClient not found in auth store. Skipping automatic refetch.');
            }
            // Call the original onSuccess if it was provided
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context);
            }
        },
        // Spread the options to allow UI components to pass their own callbacks
        ...options,
    });
};
//# sourceMappingURL=useSubscriptionReset.js.map